import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useStore } from '@/hooks/useStore';
import { useCustomer } from '@/context/CustomerContext';
import { StoreHeader, StoreFooter } from '@/components/store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { User, Package, MapPin, LogOut, Plus, Trash2, ShoppingCart } from 'lucide-react';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '@/components/ui/accordion';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { US_STATES, CA_PROVINCES, COUNTRIES } from '@/utils/locationData';
import { storePath, getSubdomain } from "../../utils/storeUtils";

export default function StoreAccount() {
    const { subdomain: paramSubdomain } = useParams();
    const subdomain = paramSubdomain || getSubdomain();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const orderIdParam = searchParams.get('order');

    const { store, theme, storeAppearance, platformInfo } = useStore(subdomain);
    const { customer, loading, logout, updateProfile, getOrders, updateAddresses } = useCustomer();
    const [orders, setOrders] = useState([]);
    const [loadingOrders, setLoadingOrders] = useState(false);
    const [selectedOrderId, setSelectedOrderId] = useState(orderIdParam);

    const [isAddingAddress, setIsAddingAddress] = useState(false);
    const [addressForm, setAddressForm] = useState({
        firstname: '',
        lastname: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        zipcode: '',
        country: 'US',
        is_default: false
    });

    useEffect(() => {
        if (!loading && !customer) {
            navigate(storePath(subdomain, `/login`));
        }
    }, [customer, loading, navigate, subdomain]);

    useEffect(() => {
        if (customer) {
            fetchOrders();
        }
    }, [customer]);

    const fetchOrders = async () => {
        setLoadingOrders(true);
        try {
            const data = await getOrders();
            // Defensively handle response structure
            if (Array.isArray(data)) setOrders(data);
            else if (data && Array.isArray(data.orders)) setOrders(data.orders);
            else setOrders([]);
        } catch (error) {
            console.error(error);
        } finally {
            setLoadingOrders(false);
        }
    };

    const handleLogout = () => {
        logout();
        navigate(storePath(subdomain, "/"));
    };

    const handleUpdateProfile = async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        try {
            await updateProfile({
                name: formData.get('name'),
                email: formData.get('email'),
                phone: formData.get('phone')
            });
            toast.success("Profile updated");
        } catch (error) {
            toast.error("Update failed");
        }
    };

    const handleAddAddress = async (e) => {
        e.preventDefault();
        try {
            const newAddresses = [...(customer.addresses || []), addressForm];
            await updateAddresses(newAddresses);
            toast.success("Address added successfully");
            setIsAddingAddress(false);
            setAddressForm({
                firstname: '', lastname: '', phone: '', address: '',
                city: '', state: '', zipcode: '', country: 'US', is_default: false
            });
        } catch (err) {
            toast.error("Failed to add address");
        }
    };

    const handleRemoveAddress = async (index) => {
        try {
            const newAddresses = [...customer.addresses];
            newAddresses.splice(index, 1);
            await updateAddresses(newAddresses);
            toast.success("Address removed");
        } catch (err) {
            toast.error("Failed to remove address");
        }
    };

    const handleSetDefaultAddress = async (index) => {
        try {
            const newAddresses = customer.addresses.map((a, i) => ({
                ...a,
                is_default: i === index
            }));
            await updateAddresses(newAddresses);
            toast.success("Default address updated");
        } catch (err) {
            toast.error("Failed to set default address");
        }
    }

    if (!customer) return null;

    return (
        <div className="animate-fade-in">

            <div className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-12">
                <div className="flex items-center justify-between mb-8">
                    <h1 className="text-3xl font-bold text-slate-900">My Account</h1>
                    <Button variant="outline" onClick={handleLogout} className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200">
                        <LogOut size={16} className="mr-2" />
                        Sign Out
                    </Button>
                </div>

                <Tabs defaultValue="orders" className="space-y-8">
                    <TabsList className="bg-slate-100 p-1 rounded-xl">
                        <TabsTrigger value="orders" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
                            <Package size={16} className="mr-2" />
                            My Orders
                        </TabsTrigger>
                        <TabsTrigger value="profile" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
                            <User size={16} className="mr-2" />
                            Profile
                        </TabsTrigger>
                        <TabsTrigger value="addresses" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
                            <MapPin size={16} className="mr-2" />
                            Addresses
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="orders">
                        <Card className="border-slate-200 shadow-sm">
                            <CardHeader>
                                <CardTitle>Order History</CardTitle>
                            </CardHeader>
                            <CardContent>
                                {loadingOrders ? (
                                    <div className="py-12 flex justify-center">
                                        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                                    </div>
                                ) : orders.length === 0 ? (
                                    <div className="text-center py-12">
                                        <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-400">
                                            <Package size={24} />
                                        </div>
                                        <h3 className="text-lg font-medium text-slate-900">No orders yet</h3>
                                        <p className="text-slate-500 mt-1">Start shopping to see your orders here.</p>
                                        <Button
                                            onClick={() => navigate(storePath(subdomain, `/shop`))}
                                            className="mt-6"
                                            style={{ backgroundColor: theme?.primary_color }}
                                        >
                                            Browse Shop
                                        </Button>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        <Accordion
                                            type="multiple"
                                            defaultValue={orderIdParam ? [`order-${orderIdParam}`] : []}
                                            className="w-full space-y-4"
                                        >
                                            {orders.map((order) => (
                                                <AccordionItem
                                                    key={order._id}
                                                    value={`order-${order.order_id}`}
                                                    className="border border-slate-200 rounded-xl overflow-hidden bg-white"
                                                >
                                                    <AccordionTrigger className="px-6 py-4 hover:bg-slate-50 hover:no-underline transition-colors flex justify-between">
                                                        <div className="flex flex-col md:flex-row md:items-center justify-between w-full pr-4 gap-4">
                                                            <div className="text-left">
                                                                <p className="font-bold text-slate-900">{order.order_id.slice(-8).toUpperCase()}</p>
                                                                <p className="text-sm text-slate-500">{new Date(order.created_at).toLocaleDateString()}</p>
                                                            </div>
                                                            <div className="flex items-center gap-6">
                                                                <div className="text-right hidden sm:block">
                                                                    <p className="text-sm text-slate-500">Total</p>
                                                                    <p className="font-bold text-slate-900">${(order.total_charged || order.total || 0).toFixed(2)}</p>
                                                                </div>
                                                                <div className="text-right">
                                                                    <p className="text-sm text-slate-500 mb-1">Status</p>
                                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                                                                        {order.status}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </AccordionTrigger>
                                                    <AccordionContent className="px-6 pb-6 pt-2 border-t border-slate-100">
                                                        <div className="space-y-4">
                                                            {/* Products List */}
                                                            <div className="space-y-4">
                                                                {order.items?.map((item, index) => (
                                                                    <div key={index} className="flex gap-4">
                                                                        <div className="w-16 h-16 rounded-md bg-slate-50 border border-slate-100 overflow-hidden flex-shrink-0">
                                                                            {item.image_url ? (
                                                                                <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                                                                            ) : (
                                                                                <Package size={20} className="w-full h-full p-4 text-slate-300" />
                                                                            )}
                                                                        </div>
                                                                        <div className="flex-1 min-w-0 flex flex-col justify-center">
                                                                            <p className="text-sm font-medium text-slate-900 line-clamp-2">{item.name}</p>
                                                                            <p className="text-sm text-slate-500 mt-1">Qty: {item.quantity}</p>

                                                                            {/* Customizations display */}
                                                                            {item.customizations && (Array.isArray(item.customizations) ? item.customizations.length > 0 : Object.keys(item.customizations).length > 0) && (
                                                                                <div className="mt-2 space-y-1 pl-2 border-l border-slate-100">
                                                                                    {Array.isArray(item.customizations) ? (
                                                                                        item.customizations.map((cust) => (
                                                                                            <div key={cust.id} className="text-[10px]">
                                                                                                <div className="flex justify-between font-bold text-slate-600">
                                                                                                    <span>{cust.name}</span>
                                                                                                    <span className="ml-2 text-indigo-600">+${cust.total.toFixed(2)}</span>
                                                                                                </div>
                                                                                                {cust.fields.map((f, i) => (
                                                                                                    <p key={i} className="text-slate-400 italic">
                                                                                                        {f.label}: {f.value}
                                                                                                    </p>
                                                                                                ))}
                                                                                            </div>
                                                                                        ))
                                                                                    ) : (
                                                                                        Object.entries(item.customizations).map(([label, val]) => (
                                                                                            <p key={label} className="text-[10px] text-slate-400 italic">
                                                                                                <span className="font-medium mr-1 text-slate-500">{label}:</span>
                                                                                                {val && typeof val === 'string' && val.includes('T') && !isNaN(Date.parse(val))
                                                                                                    ? new Date(val).toLocaleDateString()
                                                                                                    : val === true ? 'Yes' : val}
                                                                                            </p>
                                                                                        ))
                                                                                    )}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        <div className="text-right flex flex-col justify-center flex-shrink-0">
                                                                            <span className="font-medium text-slate-900">
                                                                                ${((item.display_price || item.base_price || 0) * item.quantity).toFixed(2)}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>

                                                            {/* Cost Breakdown */}
                                                            <div className="border-t border-slate-100 pt-4 space-y-2 text-sm">
                                                                <div className="flex justify-between text-slate-600">
                                                                    <span>Subtotal</span>
                                                                    <span className="font-medium text-slate-900">${(order.subtotal || order.base_total || 0).toFixed(2)}</span>
                                                                </div>
                                                                <div className="flex justify-between text-slate-600">
                                                                    <span className="flex flex-col">
                                                                        <span>Shipping</span>
                                                                    </span>
                                                                    <span className="font-medium text-slate-900">${(order.shipping || order.shipping_cost || 0).toFixed(2)}</span>
                                                                </div>
                                                            </div>

                                                            <div className="border-t border-slate-100 flex justify-between items-center bg-slate-50 -mx-6 px-6 py-4 mt-4 mb-[-24px]">
                                                                <span className="font-bold text-slate-900 text-base">Total Charged</span>
                                                                <span className="font-bold text-indigo-600 text-xl">${(order.total_charged || order.total || 0).toFixed(2)}</span>
                                                            </div>
                                                        </div>
                                                    </AccordionContent>
                                                </AccordionItem>
                                            ))}
                                        </Accordion>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>


                    <TabsContent value="profile">
                        <Card className="border-slate-200 shadow-sm max-w-2xl">
                            <CardHeader>
                                <CardTitle>Personal Details</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <form onSubmit={handleUpdateProfile} className="space-y-6">
                                    <div className="space-y-2">
                                        <Label>Full Name</Label>
                                        <Input name="name" defaultValue={customer.name} />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Email Address</Label>
                                        <Input name="email" defaultValue={customer.email} disabled className="bg-slate-50" />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Phone Number</Label>
                                        <Input name="phone" defaultValue={customer.phone} />
                                    </div>
                                    <Button type="submit" style={{ backgroundColor: theme?.primary_color }}>
                                        Save Changes
                                    </Button>
                                </form>
                            </CardContent>
                        </Card>
                    </TabsContent>

                    <TabsContent value="addresses">
                        <Card className="border-slate-200 shadow-sm">
                            <CardHeader>
                                <CardTitle>Saved Addresses</CardTitle>
                            </CardHeader>
                            <CardContent>
                                {isAddingAddress ? (
                                    <form onSubmit={handleAddAddress} className="space-y-6 max-w-2xl bg-slate-50 p-6 rounded-xl border border-slate-100">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label>First Name</Label>
                                                <Input value={addressForm.firstname} onChange={e => setAddressForm({ ...addressForm, firstname: e.target.value })} required />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Last Name</Label>
                                                <Input value={addressForm.lastname} onChange={e => setAddressForm({ ...addressForm, lastname: e.target.value })} required />
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Phone</Label>
                                            <Input value={addressForm.phone} onChange={e => setAddressForm({ ...addressForm, phone: e.target.value })} required />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Address</Label>
                                            <Input value={addressForm.address} onChange={e => setAddressForm({ ...addressForm, address: e.target.value })} required />
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label>City</Label>
                                                <Input value={addressForm.city} onChange={e => setAddressForm({ ...addressForm, city: e.target.value })} required />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>{addressForm.country === 'CA' ? 'Province' : 'State'}</Label>
                                                <Select
                                                    value={addressForm.state}
                                                    onValueChange={(val) => setAddressForm({ ...addressForm, state: val })}
                                                >
                                                    <SelectTrigger className="w-full">
                                                        <SelectValue placeholder={addressForm.country === 'CA' ? 'Province' : 'State'} />
                                                    </SelectTrigger>
                                                    <SelectContent className="max-h-[300px]">
                                                        {(addressForm.country === 'CA' ? CA_PROVINCES : US_STATES).map((state) => (
                                                            <SelectItem key={state.value} value={state.value}>
                                                                {state.label}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label>Zip Code</Label>
                                                <Input value={addressForm.zipcode} onChange={e => setAddressForm({ ...addressForm, zipcode: e.target.value })} required />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Country</Label>
                                                <Select
                                                    value={addressForm.country}
                                                    onValueChange={(val) => setAddressForm({ ...addressForm, country: val, state: '' })}
                                                >
                                                    <SelectTrigger className="w-full">
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {COUNTRIES.map(c => (
                                                            <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-4 pt-4">
                                            <Button type="button" variant="outline" onClick={() => setIsAddingAddress(false)}>Cancel</Button>
                                            <Button type="submit" style={{ backgroundColor: theme?.primary_color }}>Save Address</Button>
                                        </div>
                                    </form>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {customer.addresses?.map((addr, i) => (
                                            <div key={i} className="border border-slate-200 p-6 rounded-xl space-y-2 relative group hover:border-indigo-200 transition-colors">
                                                {addr.is_default && (
                                                    <span className="absolute top-4 right-4 text-[10px] font-bold uppercase tracking-wider bg-indigo-50 text-indigo-600 px-2 py-1 rounded-md">Default</span>
                                                )}
                                                <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                                                    {!addr.is_default && (
                                                        <Button size="sm" variant="outline" onClick={() => handleSetDefaultAddress(i)} className="h-8 text-xs">Set Default</Button>
                                                    )}
                                                    <Button size="sm" variant="destructive" onClick={() => handleRemoveAddress(i)} className="h-8 px-2"><Trash2 size={14} /></Button>
                                                </div>
                                                <p className="font-bold text-slate-900">{addr.firstname} {addr.lastname}</p>
                                                <p className="text-slate-600">{addr.address}</p>
                                                <p className="text-slate-600">{addr.city}, {addr.state} {addr.zipcode}</p>
                                                <p className="text-slate-600">{addr.country}</p>
                                                <p className="text-slate-500 text-sm mt-2">{addr.phone}</p>
                                            </div>
                                        ))}
                                        <button
                                            onClick={() => setIsAddingAddress(true)}
                                            className="border-2 border-dashed border-slate-200 rounded-xl p-6 flex flex-col items-center justify-center text-slate-400 hover:border-indigo-400 hover:text-indigo-600 hover:bg-indigo-50/50 transition-colors h-full min-h-[220px]"
                                        >
                                            <Plus size={32} className="mb-2" />
                                            <span className="font-medium">Add New Address</span>
                                        </button>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>

        </div>
    );
}
